import React, { useCallback, useEffect, useState, useMemo } from "react";
import classNames from 'classnames';
import Tooltip from "@mui/material/Tooltip";
import { format } from "date-fns";
import { useDispatch, useSelector } from "react-redux";

import { Icon, mdSize } from "../../icon";
import { useModelPopup } from "../../../common/hooks";
import mainStyles from "modules/ideasWrapper/ideasWrapper.module.scss";
import styles from './fordPlants.module.scss';
import { httpGet } from "../../httpCall";
import ForPossibleReplicationsModal from "./ForPossibleReplicationsModal.component";
import LoadingComponent from "modules/primitives/loading/loading.component";
import { updateProjectReplications } from "modules/projects/project/project.action";

const tooltipTextsByState = {
  different_solution: 'Different Solution in Place',
  under_review: 'Possible Replication',
  not_applicable: 'Not Applicable for this Plant'
}

const FordPossibleReplications = ({ canEdit, data, instance }) => {
  const dispatch = useDispatch();
  const [replicationsData, setReplicationsData] = useState(null);
  const popup = useModelPopup();
  const {
    projectsReducer: {
      projectReducer: {
        overviewPossibleReplications
      },
    },
  } = useSelector((state) => state);

  useEffect(() => {
    if (instance === 'Project') {
      setReplicationsData(overviewPossibleReplications || []);
    }
  }, [overviewPossibleReplications]);

  useEffect(() => {
    if (instance === 'Project') {
      return;
    }

    httpGet({
      call: `possible_replications/plants_and_areas?resource_id=${data.id}&resource_type=${instance}`
    }).subscribe(res => {
      setReplicationsData(res.response || []);
    });
  }, []);

  const handleAddNewReplications = (data) => {
    dispatch(updateProjectReplications(data));
    setReplicationsData(data);
  }

  const handleEditPlants = () => {
    popup.show({
      title: "Possible Plants + Areas",
      component: <ForPossibleReplicationsModal
        instance={instance}
        replicationsData={replicationsData}
        handleSave={handleAddNewReplications}
        data={data}
      />,
    });
  }

  const makeUserAndTime = useCallback((plant) => {
    const makeTime = format(new Date(plant.created_at || plant.created_at), 'EEEE, MMMM dd, yyyy p');

    if (!plant.user?.name) {
      return `Updated on ${makeTime}`;
    }

    return `Updated by ${plant.user.name} on ${makeTime}`;
  }, []);

  const makeReplicationsData = useMemo(() => {
    return replicationsData?.filter(obj => !(obj.current_user_plant && !obj.state) && !obj.discarded_at)
  }, [replicationsData]);

  return (
    <div className="d-flex flex-column mb-4">
      <div
        className={`d-flex align-items-center justify-content-between ${mainStyles.blockHeader}`}
      >
        <span className={mainStyles.blockTitle}>Possible Replications</span>
        {(canEdit || data?.possible_plants_edit_access) && (
          <Icon
            {...mdSize}
            icon="icn-edit-button"
            className={mainStyles.messageIcon}
            onClick={handleEditPlants}
          />
        )}
      </div>
      <div className={`${styles.content} ${mainStyles.blockContent}`}>
        {
          makeReplicationsData?.length > 0 ? (
            <>
              {
                makeReplicationsData.map(obj => (
                  <div key={obj.id} className="d-flex flex-column">
                    <div className="d-flex align-items-center">
                      <div className="col-8 d-flex flex-column text-center pl-0">
                        <span className="text-left">Plant</span>
                        <span className="font-weight-bold text-left">{obj.name}</span>
                      </div>
                      <div className="col-4 d-flex flex-column align-items-end pr-0">
                        <span>Status</span>
                        <Tooltip
                          title={tooltipTextsByState[obj.state]}
                          placement="top"
                        >
                          <div className={classNames(styles.plantCircle, styles[obj.state])}></div>
                        </Tooltip>
                      </div>
                    </div>
                    {
                      obj.areas?.length > 0 ? (
                        <div className="d-flex flex-column mt-3">
                          <span>Areas:</span>
                          <ul className="pl-4 mb-0">
                            {
                              obj.areas.map((area) => (
                                <li key={area.id}>
                                  {area.name}
                                </li>
                              ))
                            }
                          </ul>
                        </div>
                      ) : null
                    }
                    <span className="small text-muted mt-2">{makeUserAndTime(obj)}</span>
                    {
                      makeReplicationsData.length > 1 ? (
                        <hr className="mt-3 mb-3" />
                      ) : null
                    }
                  </div>
                ))
              }
            </>
          ) : !makeReplicationsData ? (
            <LoadingComponent customText="Getting replications..." />
          ) : 'No replications'
        }
      </div>
    </div>
  )
};

export default FordPossibleReplications;
